import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { FormGroup, type FormGroupProps, Help } from '@/components/Form'
import type { FormControlProps } from '@/infra/prop-types'
import type { FilterProps } from '@/infra/type-utils'

/**
 * Properties for a control container component.
 *
 * `M` = the model type, must be an object.
 * `F` = the field type
 * `K` = the field name (key) of the object
 * `P` = control props type, should omit the values being bound
 *  by the container.
 */
export type ContainerProps<
  M extends object,
  F,
  K extends keyof FilterProps<M, F>,
  P,
> = P &
  FormFieldProps & {
    model: M
    field: K
  }

/**
 * Props for a form field container component which composes the field.
 */
interface FormFieldContainerProps<
  M extends object,
  K extends string & keyof M,
> extends FormFieldProps {
  model: M
  field: K
  render: (controlProps: FormControlProps) => ReactNode
}

/**
 * Props being passed to the form field components.
 */
interface FormFieldProps {
  name?: string
  label?: string
  sublabel?: string
  help?: ReactNode

  /**
   * Validation error shown in place of the help, which also marks the
   * control as invalid.
   */
  error?: string
}

/**
 * Form field container that renders all components that are necessary for
 * a form field. The actual control must be rendered by the `render` prop.
 */
export const FormFieldContainer = observer(function FormFieldContainer<
  M extends object,
  K extends string & keyof M,
>({
  label,
  sublabel,
  help,
  name,
  error,
  model,
  field,
  render,
}: FormFieldContainerProps<M, K>) {
  const { controlProps } = useFormFieldContainer({ name, model, field, error })

  return (
    <FormGroupContainer
      label={label}
      sublabel={sublabel}
      error={error}
      help={help}
      htmlFor={name}
    >
      {render(controlProps)}
    </FormGroupContainer>
  )
})

interface UseFormFieldContainerProps<
  M extends object,
  K extends string & keyof M,
> {
  name?: string
  model: M
  field: K
  error?: string
}

/**
 * Wrapper for the form field hook
 */
export function useFormFieldContainer<
  M extends object,
  K extends string & keyof M,
>({ name, error }: UseFormFieldContainerProps<M, K>) {
  const controlProps: FormControlProps = { name, invalid: Boolean(error) }
  return { controlProps }
}

interface FormGroupContainerProps extends FormGroupProps {
  help?: ReactNode
  error?: string
}

/**
 * A {@link FormGroup} followed by its help or validation error.
 */
export const FormGroupContainer = observer(function FormGroupContainer({
  children,
  error,
  help,
  ...rest
}: FormGroupContainerProps) {
  return (
    <FormGroup {...rest}>
      {children}
      <Help error={error} help={help} />
    </FormGroup>
  )
})
